import { QueryObserverResult, useMutation, UseMutationResult, useQuery, useQueryClient } from 'react-query';
import {
  anonymizeApplicationUser,
  cancelUserDebt,
  getApplicationUserList,
  getUserDebts,
  getUserPayments,
  getUserProfileSettings,
  updateUserProfileSettings,
} from 'api/application-users';
import { AxiosError } from 'axios';
import { BaseQueryParams } from 'common/models';
import { Paginated } from 'common/utils';
import { ApplicationUser } from 'modules/ApplicationUsers/models/application-user';

import { useToast } from '../common/components';
import { DEFAULT_PAGINATED_LIST_ITEMS_PARAMS } from '../common/constants/common';
import { HttpException } from '../common/models/httpException';
import { useTranslations } from '../contexts/LocalizationContext';
import { DebtTableStatus } from '../modules/ApplicationUsers/EcoFee/model/enums/debt-status.enum';
import { PaymentHistoryStatus } from '../modules/ApplicationUsers/EcoFee/model/enums/payment-history-status.enum';
import { PaymentHistoryResponse } from '../modules/ApplicationUsers/EcoFee/model/payment-history.response';
import { UserDebtItemResponse } from '../modules/ApplicationUsers/EcoFee/model/user-debt-item.response';
import { ApplicationUserProfileSettingsRequest } from '../modules/ApplicationUsers/models/application-user-notification-settings.request.model';
import { ApplicationUserProfileSettingsResponse } from '../modules/ApplicationUsers/models/application-user-notification-settings.response.model';

export const CK_APPLICATION_USERS = 'applicationUsers';
export const CK_USER_DEBTS = 'userDebts';
export const CK_USER_PAYMENTS = 'userPayments';

export const useApplicationUsersList = (
  params: BaseQueryParams = DEFAULT_PAGINATED_LIST_ITEMS_PARAMS,
): QueryObserverResult<Paginated<ApplicationUser>, AxiosError<HttpException>> => {
  return useQuery(
    [CK_APPLICATION_USERS, { text: params.searchText, page: params.page, limit: params.limit }],
    () => getApplicationUserList(params),
    {
      keepPreviousData: true,
    },
  );
};

export const useAnonymizeApplicationUser = (): UseMutationResult<void, AxiosError<HttpException>, string> => {
  const translations = useTranslations();
  const { displayToast } = useToast();
  const queryClient = useQueryClient();
  return useMutation((id) => anonymizeApplicationUser(id), {
    onSuccess: async () => {
      await queryClient.invalidateQueries(CK_APPLICATION_USERS);
      displayToast('success', translations('users_anonymize_success'));
    },
    onError: (error) => {
      const toastMessage =
        error?.response?.status === 403
          ? translations('users_anonymize_forbidden_error')
          : translations('users_anonymize_error');
      displayToast('error', toastMessage);
    },
  });
};

export const useUserProfileSettings = (
  id: string,
): QueryObserverResult<ApplicationUserProfileSettingsResponse, AxiosError<HttpException>> =>
  useQuery([CK_APPLICATION_USERS, id], () => getUserProfileSettings(id));

export const useUpdateUserProfileSettings = (): UseMutationResult<
  ApplicationUserProfileSettingsResponse,
  AxiosError<HttpException>,
  { id: string; settingsRequest: ApplicationUserProfileSettingsRequest }
> => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, settingsRequest }: { id: string; settingsRequest: ApplicationUserProfileSettingsRequest }) =>
      updateUserProfileSettings(id, settingsRequest),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(CK_APPLICATION_USERS);
      },
    },
  );
};

export const useGetUserDebts = (
  userId: string | null,
  params: BaseQueryParams & { status?: DebtTableStatus } = DEFAULT_PAGINATED_LIST_ITEMS_PARAMS,
): QueryObserverResult<Paginated<UserDebtItemResponse>, AxiosError<HttpException>> =>
  useQuery(
    [CK_USER_DEBTS, { userId, page: params.page, limit: params.limit, text: params.searchText, status: params.status }],
    () => getUserDebts(userId!, params as BaseQueryParams & { status?: DebtTableStatus }),
    {
      enabled: !!userId,
      keepPreviousData: true,
    },
  );

export const useCancelUserDebt = (): UseMutationResult<
  void,
  AxiosError<HttpException>,
  { userId: string; debtId: string }
> => {
  const translations = useTranslations();
  const { displayToast } = useToast();
  const queryClient = useQueryClient();
  return useMutation(({ userId, debtId }) => cancelUserDebt(userId, debtId), {
    onSuccess: async () => {
      await queryClient.invalidateQueries(CK_USER_DEBTS);
      displayToast('success', translations('users_debt_cancel_success'));
    },
    onError: (error) => {
      const toastMessage = error.response?.data.message
        ? error.response?.data.message
        : translations('users_debt_cancel_error');
      displayToast('error', toastMessage);
    },
  });
};

export const useGetUserPayments = (
  userId: string | null,
  params: BaseQueryParams & { status?: PaymentHistoryStatus } = DEFAULT_PAGINATED_LIST_ITEMS_PARAMS,
): QueryObserverResult<Paginated<PaymentHistoryResponse>, AxiosError<HttpException>> =>
  useQuery(
    [
      CK_USER_PAYMENTS,
      { userId, page: params.page, limit: params.limit, text: params.searchText, status: params.status },
    ],
    () => getUserPayments(userId!, params),
    {
      enabled: !!userId,
      keepPreviousData: true,
    },
  );
